import React, { useEffect, useState } from 'react';
import { MODAL_TYPE, ModalWindow } from '../../modal/ModalWindow';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import './WebhookCreateComponent.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toaster } from 'rsuite';
import { useTranslation } from 'react-i18next';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import toastMessage from '../../ToastMessage';
import { GoBackBtn } from '../../header/GoBackBtn';
import { FormInput } from '../../merchants/FormInput';
import { BUTTON_TYPE, ButtonComponent } from '../../ButtonComponent';
import { WebhookCreateComponentBody } from './WebhookCreateComponentBody';
import { IEventType } from '../../../store/merchnts/merchant-interfaces';
import { fetchWebhookData, setWebhook, updateWebhook } from '../../../store/merchnts/actions/webhooks';
import { Shape } from '../../../constants/yup_types';
import { sendSentryMessage } from '../../../utils/sentry-helper';
import clsx from 'clsx';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';
import { checkIfValidUUID } from '../../../layout/core';
import { InputType } from '../../form/form-input-constants';
import { PARTNER_NAVIGATION } from '../../../constants/new-partner/partner-navigation';

interface IModalState {
	isVisible: boolean;
	modalType?: MODAL_TYPE;
	modalText?: string;
	modalTitle?: string;
	onConfirm?: () => void;
	onCancel?: () => void;
}

export interface IWebhookData {
	endpointUrl: string,
	eventName: string,
	id: string,
	mid: string,
	subscriberKey: string,
}

type WebhookType = {
	[WEBHOOK_FORM_FIELD_NAME.ENDPOINT_URL]: string
};

export enum WEBHOOK_FORM_FIELD_NAME {
	ENDPOINT_URL = 'endpointUrl'
}

const initialValues = {
	endpointUrl: ''
};
const getSchema = (t): Yup.SchemaOf<WebhookType> => Yup.object<Shape<WebhookType>>().shape({
	endpointUrl: Yup.string()
		.required(t('WEBHOOKS.URL_REQUIRED'))
		.url(t('WEBHOOKS.URL_EXAMPLE')),
});

export const WebhookCreateComponent = (): React.ReactElement => {
	const { webhookId, id: mid, partnerId } = useParams<{ id: string, webhookId: string, partnerId: string }>();
	const navigate = useNavigate();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	const { merchantState: { webhookEventTypes, isWebhookEventTypesLoading }} = useMerchants();
	const [availableEvents, setAvailableEvents] = useState<IEventType[]>([]);
	const [subscribedEvent, setSubscribedEvent] = useState<IEventType>({});
	const [isClicked, setIsClicked] = useState(false);
	const [modalState, setModalState] = useState<IModalState>({
		isVisible: false
	});

	const { t } = useTranslation();
	const { state, pathname } = useLocation();

	const formik = useFormik({
		initialValues,
		validationSchema: getSchema(t),
		onSubmit: () => void 0
	});

	const setInitialValues = (webhookData: IWebhookData): void => {
		formik.setValues({
			endpointUrl: webhookData?.endpointUrl
		});
		if (webhookEventTypes.length) {
			let newSubscribedEvent: IEventType = {};
			const newAvailableEvents = webhookEventTypes?.filter(item => {
				if (item.eventName === webhookData?.eventName) {
					newSubscribedEvent = item;
				}
				return item.eventName !== webhookData?.eventName;
			});
			if (newSubscribedEvent?.id){
				setSubscribedEvent(newSubscribedEvent);
			}
			setAvailableEvents(newAvailableEvents);
		}
	};

	useEffect(() => {
		if (checkIfValidUUID(webhookId)) {
			void fetchWebhookData(webhookId, setInitialValues);
		} else {
			setAvailableEvents(webhookEventTypes);
		}
	}, [webhookEventTypes]);

	const handleClose = (): void => {
		setModalState(prevState => ({
			...prevState,
			isVisible: false
		}));
	};

	const onAvailableEventClick = (e): void => {
		const newAvailableEvents = availableEvents?.filter(item => item?.id !== e?.id);
		if (subscribedEvent?.id) {
			setAvailableEvents([...newAvailableEvents, subscribedEvent]);
		} else {
			setAvailableEvents(newAvailableEvents);
		}
		setSubscribedEvent(e);
	};

	const onSubscribedEventClick = (): void => {
		const newAvailableEvents = [...availableEvents, subscribedEvent];
		setSubscribedEvent({});
		setAvailableEvents(newAvailableEvents);
	};

	const handleSave = async (): Promise<void> => {
		try {
			setIsClicked(true);
			const body: IEventType = {
				endpointUrl: formik.values.endpointUrl,
				eventName: subscribedEvent?.eventName,
				...(mid && { mid }),
				...(partnerId && { pid: partnerId }),
				...(checkIfValidUUID(webhookId) && { id: webhookId }),
			};

			const saveAction = checkIfValidUUID(webhookId) ? updateWebhook : setWebhook;
			await saveAction(body);

			let redirectUrl = '/developers/webhooks';

			if (mid) {
				redirectUrl = `/accounts/merchants/${mid}/${MERCHANTS_TABS.WEBHOOKS}`;
			}

			if (partnerId) {
				redirectUrl = `/partners/${partnerId}/${PARTNER_NAVIGATION.WEBHOOKS}`;
			}
			navigate(
				redirectUrl,
				{ state: { pageNumber: state?.pageNumber, pageSize: state?.pageSize }}
			);
		} catch (e) {
			sendSentryMessage({ name: `POST/PUT Webhooks error`, context: e });
			toaster.push(
				toastMessage({
					text: e.response?.data || t('WEBHOOKS.ERROR'),
					type: 'error',
					closable: true,
				}),
				{ placement: 'topEnd', duration: 0 }
			);
		} finally {
			setIsClicked(false);
		}
	};

	const handleCancel = (): void => {
		setModalState(
			{
				isVisible: true,
				modalType: MODAL_TYPE.CANCEL,
				modalText: t('WEBHOOKS.MODAL_TEXT'),
				modalTitle: t('WEBHOOKS.MODAL_TITLE'),
				onConfirm: () => navigate('/developers/webhooks/webhooks'),
				onCancel: handleClose
			}
		);
	};

	const onSaveBtnClick = (): void => {
		formik.handleSubmit();
		if (!formik.isValid) return;

		if (checkIfValidUUID(webhookId)) {
			setModalState({
				isVisible: true,
				modalType: MODAL_TYPE.SAVE,
				modalText: t('WEBHOOKS.UPDATE'),
				modalTitle: t('WEBHOOKS.EDIT_WEBHOOK'),
				onConfirm: handleSave,
				onCancel: handleClose
			});
		} else {
			void handleSave();
		}
	};

	return (
		<>
			<div className="mb-5">
				{!checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && <GoBackBtn/>}
			</div>
			<div className="card page-height">
				<div className="card-body">
					<div className="border-bottom mb-6">
						<FormInput
							formik={formik}
							label={t('WEBHOOKS.URL')}
							fieldName={WEBHOOK_FORM_FIELD_NAME.ENDPOINT_URL}
							inputType={InputType.Website}
							placeholder={t('WEBHOOKS.INPUT_PLACEHOLDER')}
							additionalClasses="col-12 col-sm-7 col-md-6"
							key={WEBHOOK_FORM_FIELD_NAME.ENDPOINT_URL}
						/>
					</div>
						<WebhookCreateComponentBody
							availableEvents={availableEvents}
							subscribedEvent={subscribedEvent}
							onAvailableEventClick={onAvailableEventClick}
							onSubscribedEventClick={onSubscribedEventClick}
							isState={checkIfValidUUID(webhookId)}
						/>
					{
						checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && (
							<ButtonComponent
								buttonName={t('WEBHOOKS.SAVE')}
								onButtonClick={onSaveBtnClick}
								loading={isClicked}
								additionalClass={clsx('btn-sm save-btn-width mt-10 d-block ms-auto',
									isWebhookEventTypesLoading && 'd-none'
								)}
								isDisabled={!subscribedEvent.id || formik.values.endpointUrl === ''}
							/>
						)
					}
				</div>
			</div>
			<div className={clsx('d-flex mt-8 justify-content-between', !checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) ? 'd-block' : 'd-none')}>
				<ButtonComponent
					buttonName={t('WEBHOOKS.CANCEL')}
					buttonType={BUTTON_TYPE.TRANSPARENT}
					onButtonClick={handleCancel}
				/>
				<ButtonComponent
					buttonName={t('WEBHOOKS.SAVE')}
					onButtonClick={onSaveBtnClick}
					loading={isClicked}
					additionalClass="btn-sm save-btn-width"
					isDisabled={!subscribedEvent.id || formik.values.endpointUrl === ''}
				/>
			</div>
			<ModalWindow
				isOpen={modalState.isVisible}
				modalTitle={modalState?.modalTitle}
				modalText={modalState?.modalText}
				modalType={modalState?.modalType}
				onClose={handleClose}
				onCancel={modalState?.onCancel}
				onConfirm={modalState?.onConfirm}
			/>
		</>
	);
};
